import { useState, useEffect } from 'react';

const FloatingWhatsApp = () => {
  const [visible, setVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const whatsappNumber = '18139526893';
  const message = encodeURIComponent("Hi MartLed! I'm interested in mobile LED advertising for my business. Can you tell me more about your campaigns?");
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;

  // Fade in after scroll
  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 200);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-show popup after 8 seconds (once)
  useEffect(() => {
    if (dismissed) return;
    const timer = setTimeout(() => {
      setShowPopup(true);
      // Auto-hide popup after 6s
      const hideTimer = setTimeout(() => setShowPopup(false), 6000);
      return () => clearTimeout(hideTimer);
    }, 8000);
    return () => clearTimeout(timer);
  }, [dismissed]);

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPopup(false);
    setDismissed(true);
  };

  return (
    <div
      className={`fixed bottom-6 left-6 z-50 flex flex-col items-start gap-3 transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
      }`}
    >
      {/* Popup Chat Bubble */}
      {showPopup && (
        <div
          className="relative max-w-xs w-72 rounded-2xl p-4 shadow-2xl animate-fadeInUp"
          style={{
            background: 'linear-gradient(135deg, #0f0f0f, #1a1a1a)',
            border: '1px solid rgba(57,255,20,0.4)',
            boxShadow: '0 0 30px rgba(57,255,20,0.2)',
          }}
        >
          {/* Close */}
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center text-gray-500 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-sm" />
          </button>

          {/* Header */}
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-10 h-10 flex items-center justify-center rounded-full shrink-0"
              style={{ backgroundColor: 'rgba(57,255,20,0.15)', border: '1px solid rgba(57,255,20,0.4)' }}
            >
              <i className="ri-truck-line text-lg" style={{ color: '#39ff14' }} />
            </div>
            <div>
              <div className="font-orbitron font-bold text-white text-sm">MartLed</div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-400 inline-block animate-pulse" />
                <span className="text-xs text-gray-400 font-rajdhani">Online now · Fast response</span>
              </div>
            </div>
          </div>

          {/* Message Bubble */}
          <div
            className="rounded-xl rounded-tl-sm px-4 py-3 mb-3 text-sm font-body leading-relaxed"
            style={{ backgroundColor: 'rgba(57,255,20,0.08)', border: '1px solid rgba(57,255,20,0.15)', color: '#e0e0e0' }}
          >
            👋 Hey there! Ready to get your brand rolling through Tampa? Let&apos;s talk campaigns!
          </div>

          {/* CTA */}
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-rajdhani font-black tracking-wider uppercase text-sm text-black transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            style={{ backgroundColor: '#39ff14', boxShadow: '0 0 20px rgba(57,255,20,0.4)' }}
          >
            <i className="ri-whatsapp-line text-base" />
            Chat on WhatsApp
          </a>
        </div>
      )}

      {/* Main WhatsApp Button */}
      <div className="relative flex items-center gap-3">
        {/* Pulse Rings */}
        <div className="relative">
          <span
            className="absolute inset-0 rounded-full animate-ping"
            style={{ backgroundColor: 'rgba(57,255,20,0.25)' }}
          />
          <span
            className="absolute inset-0 rounded-full animate-ping"
            style={{ backgroundColor: 'rgba(57,255,20,0.15)', animationDelay: '0.4s' }}
          />

          {/* Button */}
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Chat on WhatsApp"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={() => setShowPopup(false)}
            className="relative w-16 h-16 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 cursor-pointer"
            style={{
              backgroundColor: '#25D366',
              boxShadow: '0 0 30px rgba(57,255,20,0.5), 0 0 60px rgba(57,255,20,0.2)',
              border: '2px solid rgba(57,255,20,0.6)',
            }}
          >
            <i className="ri-whatsapp-line text-3xl text-white" />
          </a>
        </div>

        {/* Tooltip */}
        {showTooltip && !showPopup && (
          <div
            className="whitespace-nowrap px-4 py-2 rounded-full text-sm font-rajdhani font-bold transition-all duration-200"
            style={{
              background: 'linear-gradient(135deg, #0f0f0f, #1a1a1a)',
              border: '1px solid rgba(57,255,20,0.4)',
              color: '#39ff14',
              boxShadow: '0 0 15px rgba(57,255,20,0.2)',
            }}
          >
            Chat with us on WhatsApp
          </div>
        )}
      </div>
    </div>
  );
};

export default FloatingWhatsApp;
